import { useApi } from "@/hooks/useApi";
import { AttachmentBudgetReportDTO, LLMSelection } from "@shaarp/common";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/** Why an attachment cannot be sent, or should probably not be. */
export type AttachmentIssue =
  | { kind: "tooLarge" }
  | { kind: "noRoomLeft" }
  | { kind: "duplicate"; alreadySentAt: string };

/**
 * Where one attachment stands.
 *
 * `blocked` prevents the send, `warning` does not: a document that was already
 * sent is a waste of context, not a mistake, and the user may well have a reason
 * to send it again.
 */
export type AttachmentVerdict =
  | { status: "checking" }
  | { status: "ok" }
  | { status: "blocked"; issue: AttachmentIssue }
  | { status: "warning"; issue: AttachmentIssue };

export type AttachmentChecks = {
  /** Where each attached file stands, keyed by the `File` itself. */
  verdicts: Map<File, AttachmentVerdict>;
  /** True while at least one attachment has not come back yet. */
  isChecking: boolean;
  /** Files that must be removed before the message can be sent. */
  blockedFiles: File[];
  /** Files already sent earlier in this conversation. */
  duplicateFiles: File[];
};

type Measurement = {
  tokenEstimate: number | null;
  alreadySentAt?: string;
};

/**
 * Longest a measurement may take before it is treated as unmeasured.
 *
 * Generous on purpose: the server parses the document to answer, which is slow
 * on a large office file over a mobile connection. It only has to be shorter
 * than the user's patience.
 */
const MEASUREMENT_TIMEOUT_MS = 30_000;

/**
 * Runs every check an attachment has to pass, as soon as it is attached.
 *
 * One step, one verdict per file. Size, room left in the conversation and
 * "already sent" used to be answered at three different moments — the first in
 * the composer, the second by a modal that popped after the send, the third by
 * the server once the file was already stored — so the user met the same
 * document three times and only ever learned it was a dead end at the end.
 *
 * Why the checks cannot be a single number:
 * - A document larger than the whole window never fits, whatever is compacted
 *   away: an attachment is indivisible. That is a refusal.
 * - Two documents that each fit can still not fit *together*, or not fit next
 *   to the documents this conversation already carries. That is what the running
 *   total below catches, and it is the case the old per-file check let through:
 *   the message went out, the server compacted, and a document was silently
 *   dropped from the prompt — the assistant then answered about a file it never
 *   read. Only documents are counted, never the written history: compaction
 *   shrinks history without trouble, so counting it would refuse a document the
 *   server would have sent perfectly well.
 * - A document already sent is neither: the assistant still has it.
 *
 * Measuring happens server-side, on the same parser that produces the estimate
 * stored at upload, so the composer and the send-time check can never disagree.
 * The estimates are model-independent, so switching model re-decides the
 * verdicts from the cached figures instead of re-uploading anything.
 *
 * A measurement that fails is recorded as unknown and never blocks the send:
 * refusing on an unknown would reject documents that are perfectly fine, and
 * the send-time check still stands behind it.
 *
 * @param files Files currently attached to the composer, in attachment order.
 * @param llmSelection Model currently selected in the composer.
 * @param conversationId Thread being written in; empty for a new chat, which
 *   carries no documents yet and no earlier copy to be a duplicate of.
 */
export function useAttachmentChecks({
  files,
  llmSelection,
  conversationId,
}: {
  files: File[];
  llmSelection: LLMSelection;
  conversationId?: string;
}): AttachmentChecks {
  const api = useApi();
  const [measurements, setMeasurements] = useState<Map<File, Measurement>>(
    new Map()
  );
  const [budgets, setBudgets] = useState<Record<string, number> | null>(null);
  const [documentTokens, setDocumentTokens] = useState(0);
  // Read inside the effect so measuring never depends on a fresh state
  // identity: recording a measurement would otherwise re-trigger the check that
  // produced it.
  const measurementsRef = useRef(measurements);
  measurementsRef.current = measurements;
  // Requests still in flight. A measurement is only recorded when it comes
  // back, so without this a second attachment picked during the first check
  // would re-send the first file along with it: the effect re-runs on the new
  // `files` identity and everything unrecorded looks unmeasured.
  const inFlightRef = useRef<Set<File>>(new Set());

  const check = useCallback(
    async (unmeasured: File[]) => {
      const formData = new FormData();
      unmeasured.forEach((file) => formData.append("files", file));
      if (conversationId) formData.append("conversationId", conversationId);

      try {
        const { data } = await api.post<AttachmentBudgetReportDTO>(
          "/conversation/attachment-budget",
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
            // The shared axios instance has no timeout, so a connection that
            // dies without closing would leave the card spinning and the send
            // button inert until the user thought to remove the document. A
            // failure is the better outcome: it degrades to "unmeasured", which
            // the send-time check still stands behind.
            timeout: MEASUREMENT_TIMEOUT_MS,
          }
        );

        // Matched by position rather than by name: two files can share a name,
        // and the server answers in the order it was given them.
        setMeasurements((previous) => {
          const next = new Map(previous);
          unmeasured.forEach((file, index) => {
            const measured = data.files[index];
            next.set(file, {
              tokenEstimate: measured?.tokenEstimate ?? null,
              ...(measured?.alreadySentAt
                ? { alreadySentAt: measured.alreadySentAt }
                : {}),
            });
          });
          return next;
        });
        setBudgets(data.budgets);
        // Re-read on every check rather than once: the thread gains documents as
        // it goes, and a figure from three answers ago would leave room that is
        // no longer there.
        setDocumentTokens(data.documentTokens);
      } catch {
        // Unknown, not oversized — see the note on degradation above.
        setMeasurements((previous) => {
          const next = new Map(previous);
          unmeasured.forEach((file) => next.set(file, { tokenEstimate: null }));
          return next;
        });
      }
    },
    [api, conversationId]
  );

  useEffect(() => {
    const attached = new Set(files);
    const unmeasured = files.filter(
      (file) =>
        !measurementsRef.current.has(file) && !inFlightRef.current.has(file)
    );

    // Drop what the user removed, so a file re-attached later is re-checked
    // against a conversation that has since moved on.
    const stale = [...measurementsRef.current.keys()].filter(
      (file) => !attached.has(file)
    );
    if (stale.length > 0) {
      setMeasurements((previous) => {
        const next = new Map(previous);
        stale.forEach((file) => next.delete(file));
        return next;
      });
    }

    if (unmeasured.length > 0) {
      unmeasured.forEach((file) => inFlightRef.current.add(file));
      void check(unmeasured).finally(() =>
        unmeasured.forEach((file) => inFlightRef.current.delete(file))
      );
    }
  }, [files, check]);

  const verdicts = useMemo(() => {
    const verdictByFile = new Map<File, AttachmentVerdict>();
    const budget = budgets?.[llmSelection];
    // The documents already in the thread are counted first: compaction cannot
    // shrink them, so the new attachments only ever compete for what is left.
    let spent = documentTokens;

    for (const file of files) {
      const measurement = measurements.get(file);
      if (!measurement) {
        verdictByFile.set(file, { status: "checking" });
        continue;
      }

      const duplicate: AttachmentVerdict | null = measurement.alreadySentAt
        ? {
            status: "warning",
            issue: {
              kind: "duplicate",
              alreadySentAt: measurement.alreadySentAt,
            },
          }
        : null;

      // Nothing to judge against: an unmeasured document, or budgets that never
      // arrived. Let it through — the server refuses it if it really cannot fit.
      if (!budget || !measurement.tokenEstimate) {
        verdictByFile.set(file, duplicate ?? { status: "ok" });
        continue;
      }

      if (measurement.tokenEstimate > budget) {
        verdictByFile.set(file, {
          status: "blocked",
          issue: { kind: "tooLarge" },
        });
        continue;
      }

      // Answered before the room check, and it has to be: the copy already in
      // the thread is itself part of `documentTokens`, so a duplicate is always
      // short of room by exactly its own size. Refusing it there would say "no
      // room for this document" about a document the assistant already holds —
      // the wrong sentence, and blocking on it would be wrong too.
      if (duplicate) {
        verdictByFile.set(file, duplicate);
        continue;
      }

      if (spent + measurement.tokenEstimate > budget) {
        verdictByFile.set(file, {
          status: "blocked",
          issue: { kind: "noRoomLeft" },
        });
        continue;
      }

      // Only a document that is actually going out takes up room: a blocked one
      // is on its way to being removed, and counting it would drag down the
      // files after it as well.
      spent += measurement.tokenEstimate;
      verdictByFile.set(file, { status: "ok" });
    }

    return verdictByFile;
  }, [budgets, documentTokens, files, llmSelection, measurements]);

  return useMemo(() => {
    const blockedFiles: File[] = [];
    const duplicateFiles: File[] = [];
    let isChecking = false;

    for (const [file, verdict] of verdicts) {
      if (verdict.status === "checking") isChecking = true;
      if (verdict.status === "blocked") blockedFiles.push(file);
      if (verdict.status === "warning" && verdict.issue.kind === "duplicate") {
        duplicateFiles.push(file);
      }
    }

    return { verdicts, isChecking, blockedFiles, duplicateFiles };
  }, [verdicts]);
}
